// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file TreeListBranches.tsx
 * @input Uses React, StyleX, theme tokens
 * @output Exports TreeListBranches component (internal)
 * @position Presentational component for tree connector lines; consumed by TreeListItem.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/TreeList/TreeListItem.tsx
 * - /packages/cli/templates/blocks/components/TreeList/ (showcase blocks)
 */
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const LINE_WIDTH = 1;

/**
 * Branch margin from the left edge. No exact spacing token for 10px,
 * so we use calc(--spacing-2 + --spacing-0-5) = 8 + 2 = 10.
 */
const BRANCH_MARGIN = `calc(${spacingVars['--spacing-2']} + ${spacingVars['--spacing-0-5']})`;

/** Per-level indent width, matching --spacing-4 (16px). */
const LEVEL_INDENT = spacingVars['--spacing-4'];
// =============================================================================
// Types
// =============================================================================

// =============================================================================
// Components
// =============================================================================

/**
 * Renders vertical lines showing parent-child relationships in the tree.
 * Positioned in a full-height container to span the entire item including children.
 */
export function TreeListBranches({
  ancestorsIsLast,
  isLast: _isLast,
  nestedLevel
}) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [ancestorsIsLast.map((ancestorIsLast, level) => {
      const branchOffset = `calc(${BRANCH_MARGIN} + ${level} * ${LEVEL_INDENT})`;
      return (
        // Skip the level that the current-item connector occupies
        // (nestedLevel - 1), since that position is rendered below
        // with the correct terminus/continuation style.
        !ancestorIsLast && level !== nestedLevel - 1 && /*#__PURE__*/_jsx("div", {
          ...mergeProps({
            className: "x5yr21d x10l6tqk xfyiiit"
          }, {
            style: {
              left: branchOffset
            }
          }),
          children: /*#__PURE__*/_jsx("div", {
            ...{
              className: "x1nj7uno xu96u03 x1bpp3o7 x10l6tqk x3m8u43 x1i1rx1s x7njt3n xs02d24"
            }
          })
        }, level)
      );
    }), nestedLevel > 0 && /*#__PURE__*/_jsx("div", {
      ...mergeProps({
        className: "x5yr21d x10l6tqk xfyiiit"
      }, {
        style: {
          left: `calc(${BRANCH_MARGIN} + ${nestedLevel - 1} * ${LEVEL_INDENT})`
        }
      }),
      children: /*#__PURE__*/_jsx("div", {
        ...{
          className: "x1nj7uno xu96u03 x1bpp3o7 x10l6tqk x3m8u43 x1i1rx1s x7njt3n xs02d24"
        }
      })
    })]
  });
}
TreeListBranches.displayName = 'TreeListBranches';